// File: /js/shared/extensions/questionManager-create.js
//
// Shared creator-side ordered question manager.
// Drives the contest creator manager and the quiz_challenge creator manager
// from a single set of selectors. The runtime contest admin console emits
// the same shared markup but its kind ("contest-admin") is not in the kind
// dispatch table, so this JS deliberately ignores its rows and buttons —
// the live runtime owns its own polling/click pipeline.

const KIND_CONFIGS = {
  'contest': {
    hashAttr: 'contestHash',
    hashPayloadKey: 'contest_hash',
    actions: {
      reorder: 'reorderContestQuestions',
      attach:  'attachContestExistingQuiz',
    },
  },
  'quiz-challenge': {
    hashAttr: 'quizChallengeHash',
    hashPayloadKey: 'quiz_challenge_hash',
    actions: {
      reorder: 'reorderQuizChallengeQuestions',
      attach:  'attachQuizChallengeExistingQuiz',
    },
  },
};

let questionManagerInitialized = false;

const dragState = {
  manager: null,
  rowsContainer: null,
  current: null,
  startOrder: []
};

/* -------------------------------------------------------------------------
   Kind / config helpers
   ------------------------------------------------------------------------- */

function getManagerKind(manager) {
  if (!(manager instanceof HTMLElement)) return null;
  const kind = manager.dataset.managerKind || null;
  if (!kind || !KIND_CONFIGS[kind]) return null;
  return kind;
}

function getKindConfig(manager) {
  const kind = getManagerKind(manager);
  return kind ? KIND_CONFIGS[kind] : null;
}

function getHashFromManager(manager) {
  const config = getKindConfig(manager);
  if (!config || !(manager instanceof HTMLElement)) return '';
  return manager.dataset[config.hashAttr] || '';
}

/* -------------------------------------------------------------------------
   AJAX proxy
   ------------------------------------------------------------------------- */

function createAjaxProxy(root, actionName, extraData = {}) {
  const proxyBtn = document.createElement('button');
  proxyBtn.type = 'button';
  proxyBtn.setAttribute('data-ajax-click', 'true');
  proxyBtn.setAttribute('data-name', actionName);

  Object.entries(extraData).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    proxyBtn.dataset[key] = String(value);
  });

  root.appendChild(proxyBtn);
  proxyBtn.dispatchEvent(new Event('click', { bubbles: true }));
  proxyBtn.remove();
}

/* -------------------------------------------------------------------------
   DOM helpers
   ------------------------------------------------------------------------- */

function getManagerFromNode(node) {
  if (!(node instanceof Element)) return null;
  const manager = node.closest('[data-question-manager="true"]');
  if (!(manager instanceof HTMLElement)) return null;
  // Only managers with a known kind are owned by this JS.
  return getManagerKind(manager) ? manager : null;
}

function getRowsContainer(manager) {
  if (!(manager instanceof HTMLElement)) return null;
  return manager.querySelector('[data-question-manager-rows="true"]');
}

function getRows(container) {
  if (!(container instanceof HTMLElement)) return [];
  return Array.from(container.querySelectorAll('[data-question-manager-row="true"]'));
}

function getCandidateRows(container, draggedRow) {
  return getRows(container).filter(row => row !== draggedRow);
}

function updateDisplayedPositions(container) {
  getRows(container).forEach((row, index) => {
    const posEl = row.querySelector('[data-question-manager-position="true"]');
    if (posEl) {
      posEl.textContent = String(index + 1);
    }
  });
}

function collectOrderedRelationIds(container) {
  return getRows(container)
    .map(row => parseInt(row.dataset.relationId, 10))
    .filter(id => !Number.isNaN(id) && id > 0);
}

function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}

/* -------------------------------------------------------------------------
   Reorder persistence
   ------------------------------------------------------------------------- */

function persistOrder(manager, container, previousOrder = null) {
  const orderedIds = collectOrderedRelationIds(container);
  if (orderedIds.length === 0) return;

  if (Array.isArray(previousOrder) && arraysEqual(previousOrder, orderedIds)) {
    return;
  }

  const config = getKindConfig(manager);
  if (!config) return;

  const hash = getHashFromManager(manager);

  createAjaxProxy(manager, config.actions.reorder, {
    [config.hashPayloadKey]: hash,
    new_order: JSON.stringify(orderedIds)
  });
}

function moveRow(container, row, direction) {
  const rows = getRows(container);
  const index = rows.indexOf(row);
  if (index === -1) return false;

  if (direction === 'up') {
    if (index === 0) return false;
    const previousRow = rows[index - 1];
    container.insertBefore(row, previousRow);
    return true;
  }

  if (direction === 'down') {
    if (index >= rows.length - 1) return false;
    const nextRow = rows[index + 1];
    container.insertBefore(nextRow, row);
    return true;
  }

  return false;
}

/* -------------------------------------------------------------------------
   Drag state
   ------------------------------------------------------------------------- */

function clearDropTargets(container) {
  getRows(container).forEach(row => row.classList.remove('is-drop-target'));
}

function setDropTarget(container, row) {
  clearDropTargets(container);

  if (row instanceof HTMLElement) {
    row.classList.add('is-drop-target');
  }
}

function resolveDropRow(event, rowsContainer, draggedRow) {
  const target = event.target;
  if (!(rowsContainer instanceof HTMLElement)) return null;
  if (!(draggedRow instanceof HTMLElement)) return null;

  const candidates = getCandidateRows(rowsContainer, draggedRow);
  if (candidates.length === 0) return null;

  if (target instanceof Element) {
    const directRow = target.closest('[data-question-manager-row="true"]');
    if (
      directRow instanceof HTMLElement &&
      rowsContainer.contains(directRow) &&
      directRow !== draggedRow
    ) {
      return directRow;
    }
  }

  const y = event.clientY;
  const containerRect = rowsContainer.getBoundingClientRect();

  if (y < containerRect.top - 24 || y > containerRect.bottom + 24) {
    return null;
  }

  let nearestRow = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const row of candidates) {
    const rect = row.getBoundingClientRect();

    if (y >= rect.top && y <= rect.bottom) {
      return row;
    }

    const midpoint = rect.top + (rect.height / 2);
    const distance = Math.abs(y - midpoint);

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestRow = row;
    }
  }

  return nearestRow;
}

function isImmediateNextRow(draggedRow, targetRow) {
  if (!(draggedRow instanceof HTMLElement) || !(targetRow instanceof HTMLElement)) {
    return false;
  }

  let next = draggedRow.nextElementSibling;
  while (next instanceof HTMLElement && next.matches(':not([data-question-manager-row="true"])')) {
    next = next.nextElementSibling;
  }

  return next === targetRow;
}

function dropDraggedRow(rowsContainer, draggedRow, targetRow, clientY) {
  if (!(draggedRow instanceof HTMLElement) || !(targetRow instanceof HTMLElement)) {
    return false;
  }

  if (draggedRow === targetRow) {
    return false;
  }

  const rect = targetRow.getBoundingClientRect();
  let insertAfter = clientY > rect.top + (rect.height / 2);

  // UX rule:
  // When dragging downward onto the immediately following row,
  // dropping anywhere on that row should move the item below it.
  // Otherwise the upper half becomes a no-op, which feels broken.
  if (isImmediateNextRow(draggedRow, targetRow)) {
    insertAfter = true;
  }

  if (insertAfter) {
    const nextSibling = targetRow.nextSibling;
    if (nextSibling) {
      rowsContainer.insertBefore(draggedRow, nextSibling);
    } else {
      rowsContainer.appendChild(draggedRow);
    }
  } else {
    rowsContainer.insertBefore(draggedRow, targetRow);
  }

  return true;
}

function cleanupDragState() {
  if (dragState.rowsContainer instanceof HTMLElement) {
    clearDropTargets(dragState.rowsContainer);
  }

  if (dragState.current instanceof HTMLElement) {
    dragState.current.classList.remove('is-dragging');
  }

  dragState.manager = null;
  dragState.rowsContainer = null;
  dragState.current = null;
  dragState.startOrder = [];
}

/* -------------------------------------------------------------------------
   Click handlers
   ------------------------------------------------------------------------- */

function handleAttachButtonClick(button) {
  const manager = getManagerFromNode(button);
  if (!(manager instanceof HTMLElement)) return;

  const config = getKindConfig(manager);
  if (!config) return;

  const attachSelect = manager.querySelector('[data-question-manager-attach-select="true"]');
  if (!(attachSelect instanceof HTMLSelectElement)) return;

  const hash = getHashFromManager(manager);
  const attachRequiredMessage = manager.dataset.attachRequiredMessage || '';

  const childHash = attachSelect.value || '';
  if (!childHash) {
    if (attachRequiredMessage !== '') {
      alert(attachRequiredMessage);
    }
    return;
  }

  createAjaxProxy(manager, config.actions.attach, {
    [config.hashPayloadKey]: hash,
    child_hash: childHash
  });
}

function handleActionButtonClick(button) {
  const manager = getManagerFromNode(button);
  if (!(manager instanceof HTMLElement)) return;

  const config = getKindConfig(manager);
  if (!config) return;

  const rowsContainer = getRowsContainer(manager);
  if (!(rowsContainer instanceof HTMLElement)) return;

  const hash = getHashFromManager(manager);
  const action = button.dataset.questionManagerAction;
  const row = button.closest('[data-question-manager-row="true"]');
  if (!(row instanceof HTMLElement)) return;

  if (action === 'up' || action === 'down') {
    const previousOrder = collectOrderedRelationIds(rowsContainer);
    const changed = moveRow(rowsContainer, row, action);
    if (!changed) return;

    updateDisplayedPositions(rowsContainer);
    persistOrder(manager, rowsContainer, previousOrder);
    return;
  }

  if (action === 'persist') {
    const actionName = button.dataset.actionName || '';
    if (!actionName) return;

    const confirmMessage = button.dataset.confirmMessage || '';
    if (confirmMessage && !confirm(confirmMessage)) {
      return;
    }

    const relationId = row.dataset.relationId || '';
    if (!relationId) return;

    createAjaxProxy(manager, actionName, {
      [config.hashPayloadKey]: hash,
      relation_id: relationId
    });
  }
}

/* -------------------------------------------------------------------------
   Setup
   ------------------------------------------------------------------------- */

export function setupQuestionManagers(root = document) {
  if (questionManagerInitialized) return;
  questionManagerInitialized = true;

  document.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const attachBtn = target.closest('[data-question-manager-attach-button="true"]');
    if (attachBtn instanceof HTMLElement && getManagerFromNode(attachBtn)) {
      event.preventDefault();
      handleAttachButtonClick(attachBtn);
      return;
    }

    const actionBtn = target.closest('button[data-question-manager-action]');
    if (actionBtn instanceof HTMLElement && getManagerFromNode(actionBtn)) {
      event.preventDefault();
      event.stopPropagation();
      handleActionButtonClick(actionBtn);
    }
  });

  document.addEventListener('dragstart', event => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const handle = target.closest('[data-question-manager-drag-handle="true"]');
    if (!(handle instanceof HTMLElement)) {
      return;
    }

    const manager = getManagerFromNode(handle);
    const rowsContainer = getRowsContainer(manager);
    const row = handle.closest('[data-question-manager-row="true"]');

    if (
      !(manager instanceof HTMLElement) ||
      !(rowsContainer instanceof HTMLElement) ||
      !(row instanceof HTMLElement) ||
      !rowsContainer.contains(row)
    ) {
      event.preventDefault();
      return;
    }

    dragState.manager = manager;
    dragState.rowsContainer = rowsContainer;
    dragState.current = row;
    dragState.startOrder = collectOrderedRelationIds(rowsContainer);

    row.classList.add('is-dragging');
    clearDropTargets(rowsContainer);

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', row.dataset.relationId || '');
    }
  });

  document.addEventListener('dragover', event => {
    if (!(dragState.current instanceof HTMLElement)) return;
    if (!(dragState.rowsContainer instanceof HTMLElement)) return;

    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    const targetRow = resolveDropRow(event, dragState.rowsContainer, dragState.current);
    setDropTarget(dragState.rowsContainer, targetRow);
  });

  document.addEventListener('dragleave', event => {
    if (!(dragState.current instanceof HTMLElement)) return;
    if (!(dragState.rowsContainer instanceof HTMLElement)) return;

    const related = event.relatedTarget;
    if (!(related instanceof Node) || !dragState.rowsContainer.contains(related)) {
      clearDropTargets(dragState.rowsContainer);
    }
  });

  document.addEventListener('drop', event => {
    if (!(dragState.current instanceof HTMLElement)) return;
    if (!(dragState.manager instanceof HTMLElement)) return;
    if (!(dragState.rowsContainer instanceof HTMLElement)) return;

    event.preventDefault();

    const targetRow = resolveDropRow(event, dragState.rowsContainer, dragState.current);
    if (!(targetRow instanceof HTMLElement)) {
      cleanupDragState();
      return;
    }

    const changed = dropDraggedRow(
      dragState.rowsContainer,
      dragState.current,
      targetRow,
      event.clientY
    );

    clearDropTargets(dragState.rowsContainer);

    if (!changed) {
      cleanupDragState();
      return;
    }

    updateDisplayedPositions(dragState.rowsContainer);
    persistOrder(
      dragState.manager,
      dragState.rowsContainer,
      dragState.startOrder
    );

    cleanupDragState();
  });

  document.addEventListener('dragend', () => {
    cleanupDragState();
  });
}
